import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { supabase } from '../../lib/supabase';

const BG_STATUS = {
  clear:   { label: 'Clear',   cls: 'admin-plan-badge--done' },
  pending: { label: 'Pending', cls: 'admin-plan-badge--progress' },
  expired: { label: 'Expired', cls: 'admin-plan-badge--missing' },
};

const CONTRACT_LABEL = { w2: 'W-2', '1099': '1099', volunteer: 'Volunteer' };

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

export function CoachDirectory() {
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);

    const { data: coachProfiles } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .eq('role', 'coach')
      .order('full_name');

    if (!coachProfiles?.length) { setCoaches([]); setLoading(false); return; }

    const ids = coachProfiles.map(c => c.id);

    const [
      { data: cpRows },
      { data: empRows },
      { data: athleteRows },
    ] = await Promise.all([
      supabase.from('coach_profiles').select('id, avatar_url, credentials, archived').in('id', ids),
      supabase.from('coach_employment').select('id, contract_type, background_check_status, start_date').in('id', ids),
      supabase.from('athlete_profiles').select('id, coach_id, paused').in('coach_id', ids),
    ]);

    const cpMap  = Object.fromEntries((cpRows  ?? []).map(r => [r.id, r]));
    const empMap = Object.fromEntries((empRows ?? []).map(r => [r.id, r]));
    const athleteCountMap = {};
    (athleteRows ?? []).filter(a => !a.paused).forEach(a => {
      athleteCountMap[a.coach_id] = (athleteCountMap[a.coach_id] ?? 0) + 1;
    });

    setCoaches(coachProfiles.map(c => ({
      ...c,
      avatar_url:              cpMap[c.id]?.avatar_url ?? null,
      credentials:             cpMap[c.id]?.credentials ?? null,
      archived:                cpMap[c.id]?.archived ?? false,
      contract_type:           empMap[c.id]?.contract_type ?? null,
      background_check_status: empMap[c.id]?.background_check_status ?? null,
      start_date:              empMap[c.id]?.start_date ?? null,
      athlete_count:           athleteCountMap[c.id] ?? 0,
    })));

    setLoading(false);
  }

  const activeCoaches   = coaches.filter(c => !c.archived);
  const archivedCoaches = coaches.filter(c => c.archived);
  const displayCoaches  = showArchived ? archivedCoaches : activeCoaches;

  const totalAthletes = activeCoaches.reduce((s, c) => s + c.athlete_count, 0);
  const w2Count       = activeCoaches.filter(c => c.contract_type === 'w2').length;
  const bgClearCount  = activeCoaches.filter(c => c.background_check_status === 'clear').length;

  return (
    <Layout>
      <div style={{ marginBottom: 24 }}>
        <p className="page-eyebrow">Admin</p>
        <h1 className="page-title">Coach Management</h1>
      </div>

      {/* Summary stats */}
      <div className="admin-stats-row" style={{ marginBottom: 24 }}>
        <div className="admin-stat-card">
          <span className="admin-stat-value">{activeCoaches.length}</span>
          <span className="admin-stat-label">Active Coaches</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">{totalAthletes}</span>
          <span className="admin-stat-label">Total Athletes</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">
            {bgClearCount}<span className="admin-stat-denom">/{coaches.length}</span>
          </span>
          <span className="admin-stat-label">BG Checks Clear</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">{w2Count}</span>
          <span className="admin-stat-label">W-2 Employees</span>
        </div>
      </div>

      {/* Archive toggle */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button
          className="btn-xs btn-xs-ghost"
          onClick={() => setShowArchived(v => !v)}
        >
          {showArchived
            ? `← Active Coaches (${activeCoaches.length})`
            : `View Archived (${archivedCoaches.length})`}
        </button>
      </div>

      <div className="section-card">
        {loading ? (
          <div className="loading-screen" style={{ minHeight: 220 }}>
            <div className="loading-dot-row">
              <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
            </div>
          </div>
        ) : displayCoaches.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state-title">{showArchived ? 'No archived coaches' : 'No coaches found'}</p>
            {!showArchived && <p className="empty-state-sub">Coaches appear here once a user is assigned the <strong>coach</strong> role.</p>}
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Coach</th>
                <th>Athletes</th>
                <th>Contract</th>
                <th>Start Date</th>
                <th>BG Check</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayCoaches.map(coach => {
                const bg = coach.background_check_status
                  ? (BG_STATUS[coach.background_check_status] ?? BG_STATUS.pending)
                  : null;
                return (
                  <tr
                    key={coach.id}
                    className="tr-clickable"
                    onClick={() => navigate(`/admin/coaches/${coach.id}`)}
                  >
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div className="profile-avatar" style={{ width: 36, height: 36, fontSize: 12, flexShrink: 0, cursor: 'default' }}>
                          {coach.avatar_url
                            ? <img src={coach.avatar_url} alt={coach.full_name} />
                            : initials(coach.full_name)}
                        </div>
                        <div>
                          <div className="admin-table-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {coach.full_name}
                            {coach.archived && <span className="admin-plan-badge admin-plan-badge--missing" style={{ fontSize: 10 }}>Archived</span>}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 1 }}>{coach.email}</div>
                          {coach.credentials && (
                            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 1 }}>{coach.credentials}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="admin-stat-value" style={{ fontSize: 18 }}>{coach.athlete_count}</span>
                    </td>
                    <td>
                      {coach.contract_type
                        ? <span className="badge badge-navy">{CONTRACT_LABEL[coach.contract_type] ?? coach.contract_type}</span>
                        : <span style={{ color: 'var(--gray-300)', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--gray-600)' }}>
                      {coach.start_date
                        ? new Date(coach.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                        : <span style={{ color: 'var(--gray-300)' }}>—</span>}
                    </td>
                    <td>
                      {bg
                        ? <span className={`admin-plan-badge ${bg.cls}`}>{bg.label}</span>
                        : <span style={{ color: 'var(--gray-300)', fontSize: 12 }}>—</span>}
                    </td>
                    <td>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--gold)' }}>View →</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
